import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles, categories } from '@/mocks/articles';

const articleImages: Record<string, string> = {
  'article-zone-guide-intro': 'https://readdy.ai/api/search-image?query=Abstract%20minimal%20composition%20with%20warm%20neutral%20tones%2C%20soft%20geometric%20shapes%2C%20clean%20editorial%20design%20background%2C%20subtle%20texture%2C%20modern%20aesthetic%20with%20gentle%20gradients%2C%20high%20quality%20abstract%20wallpaper%20for%20article%20header%201&width=800&height=480&seq=article-zone-guide-intro&orientation=landscape',
  'article-zone1-tips': 'https://readdy.ai/api/search-image?query=Abstract%20minimal%20composition%20with%20soft%20cream%20and%20beige%20tones%2C%20clean%20geometric%20patterns%2C%20editorial%20design%20background%2C%20gentle%20texture%2C%20modern%20aesthetic%2C%20high%20quality%20abstract%20wallpaper%20for%20article%20header%202&width=800&height=480&seq=article-zone1-tips&orientation=landscape',
  'article-zone2-advanced': 'https://readdy.ai/api/search-image?query=Abstract%20minimal%20composition%20with%20warm%20terracotta%20tones%2C%20soft%20geometric%20layers%2C%20clean%20editorial%20design%20background%2C%20modern%20aesthetic%20with%20subtle%20gradients%2C%20high%20quality%20abstract%20wallpaper%20for%20article%20header%203&width=800&height=480&seq=article-zone2-advanced&orientation=landscape',
  'article-zone2-case': 'https://readdy.ai/api/search-image?query=Abstract%20minimal%20composition%20with%20warm%20sand%20tones%2C%20soft%20geometric%20shapes%2C%20clean%20editorial%20design%2C%20modern%20aesthetic%20with%20gentle%20textures%2C%20high%20quality%20abstract%20wallpaper%20for%20article%20header%204&width=800&height=480&seq=article-zone2-case&orientation=landscape',
  'article-zone3-insight': 'https://readdy.ai/api/search-image?query=Abstract%20minimal%20composition%20with%20deep%20warm%20burgundy%20tones%2C%20soft%20geometric%20layers%2C%20editorial%20design%20background%2C%20modern%20aesthetic%2C%20high%20quality%20abstract%20wallpaper%20for%20article%20header%205&width=800&height=480&seq=article-zone3-insight&orientation=landscape',
  'article-zone3-review': 'https://readdy.ai/api/search-image?query=Abstract%20minimal%20composition%20with%20warm%20ochre%20tones%2C%20soft%20geometric%20patterns%2C%20clean%20editorial%20design%2C%20modern%20aesthetic%20with%20subtle%20textures%2C%20high%20quality%20abstract%20wallpaper%20for%20article%20header%206&width=800&height=480&seq=article-zone3-review&orientation=landscape',
  'article-comparison': 'https://readdy.ai/api/search-image?query=Abstract%20minimal%20composition%20with%20warm%20neutral%20tones%2C%20soft%20geometric%20shapes%20overlapping%2C%20clean%20editorial%20design%20background%2C%20modern%20aesthetic%2C%20high%20quality%20abstract%20wallpaper%20for%20article%20header%207&width=800&height=480&seq=article-comparison&orientation=landscape',
  'article-standard': 'https://readdy.ai/api/search-image?query=Abstract%20minimal%20composition%20with%20warm%20beige%20tones%2C%20soft%20geometric%20layers%2C%20clean%20editorial%20design%2C%20modern%20aesthetic%20with%20gentle%20gradients%2C%20high%20quality%20abstract%20wallpaper%20for%20article%20header%208&width=800&height=480&seq=article-standard&orientation=landscape',
};

const colorMap: Record<string, { tab: string; bg: string; badge: string }> = {
  accent: {
    tab: 'bg-accent-100 text-accent-800',
    bg: 'bg-accent-50',
    badge: 'bg-accent-100 text-accent-700',
  },
  primary: {
    tab: 'bg-primary-500 text-background-50',
    bg: 'bg-primary-50',
    badge: 'bg-primary-100 text-primary-700',
  },
  secondary: {
    tab: 'bg-secondary-100 text-secondary-800',
    bg: 'bg-secondary-50',
    badge: 'bg-secondary-100 text-secondary-700',
  },
};

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'zone-1');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const filteredArticles = articles.filter((a) => a.category === activeSlug);

  useEffect(() => {
    const cat = categories.find((c) => c.slug === activeSlug);
    if (cat) {
      document.title = `${cat.name}${cat.subtitle} | 一区二区三区分区指南`;
    }
  }, [activeSlug]);

  if (!currentCategory) {
    return (
      <>
        <Header />
        <div className="pt-20 text-center py-20">
          <p className="text-foreground-500">未找到该分区</p>
        </div>
        <Footer />
      </>
    );
  }

  const colors = colorMap[currentCategory.color] || colorMap.accent;
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: currentCategory.name },
  ];

  const breadcrumbSchemaData = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hnatawindow.cn/' },
      { '@type': 'ListItem', position: 2, name: currentCategory.name, item: `https://hnatawindow.cn/category/${currentCategory.slug}` },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchemaData} />
      <Header />

      <main className="pt-16">
        <div className={`${colors.bg} py-16 md:py-20`}>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
            <div className="mt-6 text-center">
              <span className={`inline-block px-3 py-1 rounded-full ${colors.badge} text-xs font-medium mb-4`}>
                {currentCategory.subtitle}
              </span>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                {currentCategory.name}导航指南
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                {currentCategory.description}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200 sticky top-16 z-40">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 py-3 overflow-x-auto">
              {categories.map((cat) => {
                const isActive = cat.slug === activeSlug;
                const catColors = colorMap[cat.color] || colorMap.accent;
                return (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      isActive
                        ? catColors.tab
                        : 'text-foreground-500 hover:text-foreground-800 hover:bg-background-100'
                    }`}
                  >
                    {cat.name}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-8">
              <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-900">
                共 {filteredArticles.length} 篇文章
              </h2>
              <ul className="mt-2 flex flex-wrap gap-2">
                {currentCategory.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-1.5 text-xs text-foreground-500">
                    <i className="ri-check-line text-accent-500 text-sm"></i>
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            {filteredArticles.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-foreground-400">该分区暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-2xl border border-background-200 overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={articleImages[article.imageSeq] || articleImages['article-zone-guide-intro']}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${colors.badge}`}>
                          {currentCategory.name}
                        </span>
                        <span className="text-xs text-foreground-400">{article.readTime} 分钟阅读</span>
                      </div>
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed mb-4">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground-400">{article.date}</span>
                        <span className="text-xs text-foreground-400">{article.author}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}